import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type AuthStep = 'initial' | 'credentials' | 'workspace'

interface Workspace {
  id: string
  name: string
}

interface AuthState {
  accessToken: string | null
  userId: string | null
  /** Short-lived proof of login/registration. Memory only (never persisted); cleared once an access token exists. */
  loginToken: string | null
  email: string | null
  authStep: AuthStep
  workspaces: Workspace[]
  isLoading: boolean
  error: string | null

  setAccessToken: (token: string | null) => void
  setUserId: (userId: string | null) => void
  setLoginToken: (token: string | null) => void
  setEmail: (email: string | null) => void
  setAuthStep: (step: AuthStep) => void
  setWorkspaces: (workspaces: Workspace[]) => void
  setLoading: (isLoading: boolean) => void
  setError: (error: string | null) => void
  logout: () => void
  reset: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      userId: null,
      loginToken: null,
      email: null,
      authStep: 'initial',
      workspaces: [],
      isLoading: true,
      error: null,

      setAccessToken: (token) => set({ accessToken: token }),
      setUserId: (userId) => set({ userId }),
      setLoginToken: (loginToken) => set({ loginToken }),
      setEmail: (email) => set({ email }),
      setAuthStep: (step) => set({ authStep: step }),
      setWorkspaces: (workspaces) => set({ workspaces }),
      setLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error }),

      logout: () =>
        set({
          accessToken: null,
          userId: null,
          loginToken: null,
          email: null,
          authStep: 'initial',
          workspaces: [],
          error: null,
        }),

      reset: () =>
        set({
          accessToken: null,
          userId: null,
          loginToken: null,
          email: null,
          authStep: 'initial',
          workspaces: [],
          isLoading: false,
          error: null,
        }),
    }),
    {
      name: 'auth',
      // Only the user ID is persisted so it survives page refresh.
      // Access token and all other auth state remain in memory as before.
      partialize: (state) => ({
        userId: state.userId,
        email: state.email,
      }),
    }
  )
)